<template>
  <div class="space-y-8">
    <!-- Price & Signature Date -->
    <div>
      <div class="flex items-center gap-2 mb-3">
        <CurrencyEuroIcon class="h-5 w-5 text-indigo-500" />
        <h4 class="text-base font-semibold tracking-tight text-gray-900">{{ $gettext("Transaction") }}</h4>
      </div>
      <div class="border border-gray-200 rounded-lg p-4">
        <div class="grid grid-cols-2 gap-3">
          <ExtractionInput v-model="data.price" type="number" :label="$gettext('Prix de l\'offre')" />
          <ExtractionInput v-model="data.signatureDate" type="date" :label="$gettext('Date de signature')" />
          <div>
            <ExtractionInput v-model="data.notaryId" type="select" :label="$gettext('Notaire')" :options="notaryOptions" />
            <button type="button" class="mt-1 w-fit rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-500" @click="showCreateNotaryModal = true">
              + {{ $gettext("Créer un notaire") }}
            </button>
          </div>
        </div>
      </div>
      <CreateNotaryModal
        :open="showCreateNotaryModal"
        @close="showCreateNotaryModal = false"
        @created="onNotaryCreated"
      />
    </div>

    <!-- Buyers -->
    <div>
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <UserGroupIcon class="h-5 w-5 text-indigo-500" />
          <h4 class="text-base font-semibold tracking-tight text-gray-900">{{ $gettext("Acheteurs") }}</h4>
        </div>
        <span class="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
          {{ data.buyers.length }}
        </span>
      </div>

      <div v-for="(buyer, index) in data.buyers" :key="index" class="border border-gray-200 rounded-lg mb-3 overflow-hidden border-l-2 border-l-indigo-200">

        <!-- ===== Company buyer card ===== -->
        <template v-if="buyer.type === 'company'">
          <!-- Collapsible header -->
          <div class="flex items-center bg-gray-50/80">
            <button
              type="button"
              class="flex-1 flex items-center justify-between px-4 py-3 hover:bg-gray-100/80 text-left transition-colors duration-150"
              @click="expandedBuyers[index] = !expandedBuyers[index]"
            >
              <div class="flex items-center gap-3">
                <BriefcaseIcon class="h-4 w-4 text-gray-400 shrink-0" />
                <span class="text-sm font-medium text-gray-700">
                  {{ $gettext("Société") }} {{ index + 1 }}
                  <span v-if="buyer.companyName" class="text-gray-500">— {{ buyer.companyName }}</span>
                </span>
                <span v-if="buyer.matchedCompanyId" class="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                  {{ $gettext("Existante") }}
                </span>
                <span v-else class="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
                  {{ $gettext("Sera créée") }}
                </span>
              </div>
              <ChevronDownIcon class="h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200" :class="expandedBuyers[index] ? 'rotate-180' : ''" />
            </button>
            <button type="button" class="px-3 py-3 text-gray-400 hover:text-red-500 transition-colors" @click.stop="removeBuyer(index)">
              <TrashIcon class="h-4 w-4" />
            </button>
          </div>

          <!-- Card body -->
          <div v-show="expandedBuyers[index]" class="p-4">
            <div class="grid grid-cols-2 gap-3">
              <ExtractionInput v-model="buyer.companyName" :label="$gettext('Dénomination sociale')" required :error="!buyer.matchedCompanyId && !buyer.companyName?.trim()" />
              <ExtractionInput v-model="buyer.companyNumber" :label="$gettext('Numéro d\'entreprise')" />
              <div class="col-span-2">
                <ExtractionAddressAutocomplete v-model="buyer.companyAddress.street" :label="$gettext('Adresse')" :placeholder="$gettext('Rue')" required :error="!buyer.matchedCompanyId && !buyer.companyAddress.street?.trim()" @address-selected="(a) => { buyer.companyAddress.city = a.city; buyer.companyAddress.zipcode = a.zipcode; }" />
                <div class="grid grid-cols-2 gap-2 mt-1">
                  <ExtractionInput v-model="buyer.companyAddress.zipcode" :placeholder="$gettext('Code postal')" required :error="!buyer.matchedCompanyId && !buyer.companyAddress.zipcode?.trim()" />
                  <ExtractionInput v-model="buyer.companyAddress.city" :placeholder="$gettext('Ville')" required :error="!buyer.matchedCompanyId && !buyer.companyAddress.city?.trim()" />
                </div>
              </div>
              <!-- Required fields dropdowns (only for new companies) -->
              <template v-if="!buyer.matchedCompanyId">
                <ExtractionInput v-model="buyer.companyTypeId" type="select" :label="$gettext('Forme juridique')" required :error="!buyer.companyTypeId" :options="companyTypeOptions" />
                <ExtractionInput v-model="buyer.industrySectorId" type="select" :label="$gettext('Secteur d\'activité')" required :error="!buyer.industrySectorId" :options="industrySectorOptions" />
              </template>
            </div>

            <!-- Signatory sub-section -->
            <div v-if="buyer.representative" class="mt-4 pt-4 border-t border-gray-200">
              <h5 class="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">{{ $gettext("Signataire") }}</h5>
              <div class="grid grid-cols-2 gap-3">
                <ExtractionInput v-model="buyer.representative.lastName" :label="$gettext('Nom')" />
                <ExtractionInput v-model="buyer.representative.firstName" :label="$gettext('Prénom')" />
                <ExtractionInput v-model="buyer.representative.role" :label="$gettext('Fonction')" />
                <ExtractionInput v-model="buyer.representative.birthdate" type="date" :label="$gettext('Date de naissance')" required :error="!buyer.representative.matchedPhysicalPersonId && (buyer.representative.firstName?.trim() || buyer.representative.lastName?.trim()) && !buyer.representative.birthdate" />
                <ExtractionInput v-model="buyer.representative.birthCity" :label="$gettext('Lieu de naissance')" />
                <ExtractionInput v-model="buyer.representative.nationalityCountryId" type="select" :label="$gettext('Nationalité')" :options="nationalityCountryOptions" required :error="!buyer.representative.matchedPhysicalPersonId && (buyer.representative.firstName?.trim() || buyer.representative.lastName?.trim()) && !buyer.representative.nationalityCountryId" />
                <div class="col-span-2">
                  <ExtractionAddressAutocomplete v-model="buyer.representative.address.street" :label="$gettext('Adresse')" :placeholder="$gettext('Rue')" required :error="!buyer.representative.matchedPhysicalPersonId && !buyer.representative.address.street?.trim()" @address-selected="(a) => { buyer.representative.address.city = a.city; buyer.representative.address.zipcode = a.zipcode; }" />
                  <div class="grid grid-cols-2 gap-2 mt-1">
                    <ExtractionInput v-model="buyer.representative.address.zipcode" :placeholder="$gettext('Code postal')" required :error="!buyer.representative.matchedPhysicalPersonId && !buyer.representative.address.zipcode?.trim()" />
                    <ExtractionInput v-model="buyer.representative.address.city" :placeholder="$gettext('Ville')" required :error="!buyer.representative.matchedPhysicalPersonId && !buyer.representative.address.city?.trim()" />
                  </div>
                </div>
                <!-- PEP questions for representative (only for new physical persons) -->
                <template v-if="!buyer.representative.matchedPhysicalPersonId">
                  <ExtractionInput v-model="buyer.representative.isPoliticalyExposed" type="boolean" :label="$gettext('Politiquement exposée')" required :error="buyer.representative.isPoliticalyExposed === null" />
                  <ExtractionInput v-model="buyer.representative.isCloseToPoliticalyExposed" type="boolean" :label="$gettext('Proche d\'un personne politiquement exposée')" required :error="buyer.representative.isCloseToPoliticalyExposed === null" />
                  <ExtractionInput v-model="buyer.representative.identificationType" type="select" :label="$gettext('Moyen d\'identification')" required :error="!buyer.representative.identificationType" :options="identificationTypeOptions" />
                  <ExtractionInput v-model="buyer.representative.professionId" type="select" :label="$gettext('Profession')" required :error="!buyer.representative.professionId" :options="professionOptions" />
                  <ExtractionInput v-model="buyer.representative.industrySectorId" type="select" :label="$gettext('Secteur d\'activité')" required :error="!buyer.representative.industrySectorId" :options="industrySectorOptions" />
                </template>
                <!-- ID document upload for representative -->
                <IdentityDocumentUpload
                  :person="buyer"
                  :person-index="index"
                  :is-extracting="extractingIdentityIndex === index"
                  :identity-error="identityErrorIndex === index ? identityError : null"
                  :label="$gettext('Documents d\'identité du signataire')"
                  @add-file="({ event }) => onIdentityFileAdd(event, index)"
                  @remove-file="({ fileIndex }) => removeIdentityFile(index, fileIndex)"
                  @extract="() => onExtractIdentity(index)"
                />
              </div>
            </div>
          </div>
        </template>

        <!-- ===== Physical person buyer card ===== -->
        <template v-else>
          <!-- Collapsible header -->
          <div class="flex items-center bg-gray-50/80">
            <button
              type="button"
              class="flex-1 flex items-center justify-between px-4 py-3 hover:bg-gray-100/80 text-left transition-colors duration-150"
              @click="expandedBuyers[index] = !expandedBuyers[index]"
            >
              <div class="flex items-center gap-3">
                <UserIcon class="h-4 w-4 text-gray-400 shrink-0" />
                <span class="text-sm font-medium text-gray-700">
                  {{ $gettext("Acheteur") }} {{ index + 1 }}
                  <span v-if="buyer.firstName || buyer.lastName" class="text-gray-500">— {{ [buyer.firstName, buyer.lastName].filter(Boolean).join(' ') }}</span>
                </span>
                <span v-if="buyer.matchConfidence === 'high'" class="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                  {{ $gettext("Existant") }}
                </span>
                <span v-else-if="buyer.matchConfidence === 'medium'" class="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                  {{ $gettext("Correspondance partielle") }}
                </span>
                <span v-else class="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
                  {{ $gettext("Sera créé") }}
                </span>
              </div>
              <ChevronDownIcon class="h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200" :class="expandedBuyers[index] ? 'rotate-180' : ''" />
            </button>
            <button type="button" class="px-3 py-3 text-gray-400 hover:text-red-500 transition-colors" @click.stop="removeBuyer(index)">
              <TrashIcon class="h-4 w-4" />
            </button>
          </div>

          <!-- Card body -->
          <div v-show="expandedBuyers[index]" class="p-4">
            <div class="grid grid-cols-2 gap-3">
              <ExtractionInput v-model="buyer.lastName" :label="$gettext('Nom')" />
              <ExtractionInput v-model="buyer.firstName" :label="$gettext('Prénom')" />
              <ExtractionInput v-model="buyer.birthdate" type="date" :label="$gettext('Date de naissance')" required :error="!buyer.matchedPhysicalPersonId && (buyer.firstName?.trim() || buyer.lastName?.trim()) && !buyer.birthdate" />
              <ExtractionInput v-model="buyer.birthCity" :label="$gettext('Lieu de naissance')" />
              <ExtractionInput v-model="buyer.nationalNumber" :label="$gettext('Numéro national')" />
              <ExtractionInput v-model="buyer.nationalityCountryId" type="select" :label="$gettext('Nationalité')" :options="nationalityCountryOptions" required :error="!buyer.matchedPhysicalPersonId && (buyer.firstName?.trim() || buyer.lastName?.trim()) && !buyer.nationalityCountryId" />
              <div class="col-span-2">
                <ExtractionAddressAutocomplete v-model="buyer.address.street" :label="$gettext('Adresse')" :placeholder="$gettext('Rue')" required :error="!buyer.matchedPhysicalPersonId && !buyer.address.street?.trim()" @address-selected="(a) => { buyer.address.city = a.city; buyer.address.zipcode = a.zipcode; }" />
                <div class="grid grid-cols-2 gap-2 mt-1">
                  <ExtractionInput v-model="buyer.address.zipcode" :placeholder="$gettext('Code postal')" required :error="!buyer.matchedPhysicalPersonId && !buyer.address.zipcode?.trim()" />
                  <ExtractionInput v-model="buyer.address.city" :placeholder="$gettext('Ville')" required :error="!buyer.matchedPhysicalPersonId && !buyer.address.city?.trim()" />
                </div>
              </div>
              <!-- Required fields (only for new physical persons) -->
              <template v-if="!buyer.matchedPhysicalPersonId">
                <ExtractionInput v-model="buyer.isPoliticalyExposed" type="boolean" :label="$gettext('Politiquement exposée')" required :error="buyer.isPoliticalyExposed === null" />
                <ExtractionInput v-model="buyer.isCloseToPoliticalyExposed" type="boolean" :label="$gettext('Proche d\'un personne politiquement exposée')" required :error="buyer.isCloseToPoliticalyExposed === null" />
                <ExtractionInput v-model="buyer.identificationType" type="select" :label="$gettext('Moyen d\'identification')" required :error="!buyer.identificationType" :options="identificationTypeOptions" />
                <ExtractionInput v-model="buyer.professionId" type="select" :label="$gettext('Profession')" required :error="!buyer.professionId" :options="professionOptions" />
                <ExtractionInput v-model="buyer.industrySectorId" type="select" :label="$gettext('Secteur d\'activité')" required :error="!buyer.industrySectorId" :options="industrySectorOptions" />
              </template>
              <!-- ID document upload -->
              <IdentityDocumentUpload
                :person="buyer"
                :person-index="index"
                :is-extracting="extractingIdentityIndex === index"
                :identity-error="identityErrorIndex === index ? identityError : null"
                @add-file="({ event }) => onIdentityFileAdd(event, index)"
                @remove-file="({ fileIndex }) => removeIdentityFile(index, fileIndex)"
                @extract="() => onExtractIdentity(index)"
              />
            </div>
          </div>
        </template>

      </div>
      <p v-if="!isValid" class="mt-2 text-xs text-red-600">
        {{ $gettext("Veuillez remplir tous les champs obligatoires (*) avant de confirmer.") }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, watch, toRaw } from "vue";
import { CurrencyEuroIcon, UserGroupIcon, BriefcaseIcon, UserIcon, ChevronDownIcon, TrashIcon } from "@heroicons/vue/24/outline";
import { useIndustrySectorStore } from "@/stores/industrySector";
import { useProfessionStore } from "@/stores/profession";
import { useCompanyTypeStore } from "@/stores/companyType";
import { useCountryStore } from "@/stores/country";
import { useNotaryStore } from "@/stores/notary";
import ExtractionInput from "@/components/ui/ExtractionInput.vue";
import ExtractionAddressAutocomplete from "@/components/ui/ExtractionAddressAutocomplete.vue";
import IdentityDocumentUpload from "@/components/ui/IdentityDocumentUpload.vue";
import CreateNotaryModal from "@/components/notaries/CreateNotaryModal.vue";

const props = defineProps({
  extractionData: { type: Object, required: true }
});

const emit = defineEmits(["extract-identity"]);

const industrySectorStore = useIndustrySectorStore();
const professionStore = useProfessionStore();
const companyTypeStore = useCompanyTypeStore();
const countryStore = useCountryStore();
const notaryStore = useNotaryStore();

// Fetch Belgian company types when countries are loaded
watch(() => countryStore.sortedCountries, (countries) => {
  if (!countries?.length) return;
  const belgium = countries.find(c => c.attributes.code === "BE");
  if (belgium) companyTypeStore.fetchCompanyTypes(belgium.id);
}, { immediate: true });

const industrySectorOptions = computed(() =>
  industrySectorStore.industrySectors.map(is => ({ value: is.id, label: is.attributes.name }))
);
const professionOptions = computed(() =>
  professionStore.professions.map(p => ({ value: p.id, label: p.attributes.name }))
);
const companyTypeOptions = computed(() =>
  companyTypeStore.companyTypes.map(ct => ({ value: ct.id, label: ct.attributes.name }))
);
const nationalityCountryOptions = computed(() =>
  countryStore.sortedCountries.map(c => ({ value: c.id, label: c.attributes.demonym || c.attributes.name }))
);
const notaryOptions = computed(() =>
  notaryStore.sorted.map(n => ({ value: n.id, label: "(" + n.attributes.zipcode + ") " + n.attributes.name }))
);
const showCreateNotaryModal = ref(false);
const onNotaryCreated = (created) => {
  if (created?.id) data.notaryId = created.id;
};
const identificationTypeOptions = [
  { value: "faceToFace", label: "Face à face" },
  { value: "remote", label: "A distance" },
  { value: "none", label: "Aucun" }
];

const data = reactive({
  price: props.extractionData.price || null,
  signatureDate: props.extractionData.signatureDate || "",
  notaryId: props.extractionData.matchedNotaryId || null,
  buyers: (props.extractionData.buyers || []).map(b => ({
    ...b,
    type: b.type || "physicalPerson",
    identityFiles: [],
    identityExtracted: false,
    companyName: b.companyName || "",
    companyNumber: b.companyNumber || "",
    companyTypeId: b.companyTypeId || null,
    industrySectorId: b.industrySectorId || null,
    companyAddress: {
      street: b.companyAddress?.street || "",
      city: b.companyAddress?.city || "",
      zipcode: b.companyAddress?.zipcode || ""
    },
    representative: b.representative ? {
      ...b.representative,
      role: b.representative.role || "",
      birthCity: b.representative.birthCity || "",
      birthdate: b.representative.birthdate || "",
      isPoliticalyExposed: b.representative.isPoliticalyExposed ?? null,
      isCloseToPoliticalyExposed: b.representative.isCloseToPoliticalyExposed ?? null,
      identificationType: b.representative.identificationType || null,
      professionId: b.representative.professionId || null,
      nationalityCountryId: b.representative.nationalityCountryId || null,
      industrySectorId: b.representative.industrySectorId || null,
      address: {
        street: b.representative.address?.street || "",
        city: b.representative.address?.city || "",
        zipcode: b.representative.address?.zipcode || ""
      }
    } : null,
    isPoliticalyExposed: b.isPoliticalyExposed ?? null,
    isCloseToPoliticalyExposed: b.isCloseToPoliticalyExposed ?? null,
    identificationType: b.identificationType || null,
    professionId: b.professionId || null,
    nationalityCountryId: b.nationalityCountryId || null,
    birthCity: b.birthCity || "",
    birthdate: b.birthdate || "",
    nationalNumber: b.nationalNumber || "",
    address: { street: b.address?.street || "", city: b.address?.city || "", zipcode: b.address?.zipcode || "" }
  }))
});

const extractingIdentityIndex = ref(null);
const identityError = ref(null);
const identityErrorIndex = ref(null);

// Collapsible state — all expanded by default
const expandedBuyers = ref(data.buyers.map(() => true));

const removeBuyer = (index) => {
  data.buyers.splice(index, 1);
  expandedBuyers.value.splice(index, 1);
};

const isValid = computed(() => {
  return data.buyers.every(buyer => {
    if (buyer.type === "company") {
      if (!buyer.matchedCompanyId) {
        if (!buyer.companyName?.trim()) return false;
        if (!buyer.companyAddress.street?.trim()) return false;
        if (!buyer.companyAddress.zipcode?.trim()) return false;
        if (!buyer.companyAddress.city?.trim()) return false;
        if (!buyer.companyTypeId) return false;
        if (!buyer.industrySectorId) return false;
      }
      if (buyer.representative && !buyer.representative.matchedPhysicalPersonId) {
        if (!buyer.representative.firstName?.trim() && !buyer.representative.lastName?.trim()) return false;
        if (buyer.representative.firstName?.trim() || buyer.representative.lastName?.trim()) {
          if (!buyer.representative.birthdate) return false;
          if (!buyer.representative.nationalityCountryId) return false;
        }
        if (!buyer.representative.address.street?.trim()) return false;
        if (!buyer.representative.address.zipcode?.trim()) return false;
        if (!buyer.representative.address.city?.trim()) return false;
        if (buyer.representative.isPoliticalyExposed === null) return false;
        if (buyer.representative.isCloseToPoliticalyExposed === null) return false;
        if (!buyer.representative.identificationType) return false;
        if (!buyer.representative.professionId) return false;
        if (!buyer.representative.industrySectorId) return false;
      }
      return true;
    } else {
      if (buyer.matchedPhysicalPersonId) return true;
      if (!buyer.firstName?.trim() && !buyer.lastName?.trim()) return true;
      if (!buyer.birthdate) return false;
      if (!buyer.nationalityCountryId) return false;
      if (!buyer.address.street?.trim()) return false;
      if (!buyer.address.zipcode?.trim()) return false;
      if (!buyer.address.city?.trim()) return false;
      if (buyer.isPoliticalyExposed === null) return false;
      if (buyer.isCloseToPoliticalyExposed === null) return false;
      if (!buyer.identificationType) return false;
      if (!buyer.professionId) return false;
      if (!buyer.industrySectorId) return false;
      return true;
    }
  });
});

const onIdentityFileAdd = (event, buyerIndex) => {
  const file = event.target.files[0];
  if (!file) return;
  event.target.value = "";

  const buyer = data.buyers[buyerIndex];
  if (!buyer || buyer.identityFiles.length >= 2) return;

  buyer.identityFiles.push(file);
  buyer.identityExtracted = false;
  identityError.value = null;
  identityErrorIndex.value = null;
};

const removeIdentityFile = (buyerIndex, fileIndex) => {
  const buyer = data.buyers[buyerIndex];
  if (!buyer) return;
  buyer.identityFiles.splice(fileIndex, 1);
  buyer.identityExtracted = false;
};

const onExtractIdentity = (buyerIndex) => {
  const buyer = data.buyers[buyerIndex];
  if (!buyer || buyer.identityFiles.length === 0) return;

  identityError.value = null;
  identityErrorIndex.value = null;
  extractingIdentityIndex.value = buyerIndex;

  emit("extract-identity", { files: buyer.identityFiles.map(f => toRaw(f)), buyerIndex });
};

const mergeIdentityData = (buyerIndex, identityData) => {
  extractingIdentityIndex.value = null;
  const buyer = data.buyers[buyerIndex];
  if (!buyer) return;

  buyer.identityExtracted = true;

  const target = buyer.type === "company" && buyer.representative ? buyer.representative : buyer;

  if (identityData.lastName) target.lastName = identityData.lastName;
  if (identityData.firstName) target.firstName = identityData.firstName;
  if (identityData.birthdate) target.birthdate = identityData.birthdate;
  if (identityData.birthCity) target.birthCity = identityData.birthCity;
  if (identityData.nationalNumber) target.nationalNumber = identityData.nationalNumber;
  if (identityData.nationalityCountryId) target.nationalityCountryId = identityData.nationalityCountryId;
  if (identityData.address?.street) target.address.street = identityData.address.street;
  if (identityData.address?.city) target.address.city = identityData.address.city;
  if (identityData.address?.zipcode) target.address.zipcode = identityData.address.zipcode;

  if (identityData.matchedPhysicalPersonId) {
    target.matchedPhysicalPersonId = identityData.matchedPhysicalPersonId;
    target.matchedPhysicalPersonName = identityData.matchedPhysicalPersonName;
    target.matchConfidence = identityData.matchConfidence;
  }
};

const onIdentityError = (buyerIndex, message) => {
  extractingIdentityIndex.value = null;
  identityError.value = message;
  identityErrorIndex.value = buyerIndex;
};

const getReviewedData = () => {
  return {
    price: data.price,
    signatureDate: data.signatureDate,
    notaryId: data.notaryId,
    buyers: data.buyers
  };
};

const getIdentityFilesPerBuyer = () => {
  return data.buyers.map((b, idx) => ({
    buyerIndex: idx,
    buyerType: b.type,
    files: (b.identityFiles || []).map(f => toRaw(f))
  }));
};

defineExpose({ getReviewedData, mergeIdentityData, onIdentityError, isValid, getIdentityFilesPerBuyer });
</script>
